import { apiV2, errorHandler } from './index';
import {
  API_ERROR,
  FETCH_ALL_GOAL_CONTENT,
  FETCH_FEATURE_FLAGS,
  UPDATE_FEATURE_FLAG,
} from '../constants/ActionTypes';
import Swal from 'sweetalert2';

// Per-goal onboarding content management. Backed by the APIv2 admin endpoints:
//   GET /admin/goal-content
//   PUT /admin/goal-content/sort-order
//   POST /admin/goal-content
//   PUT /admin/goal-content/{slug}
//   GET /admin/feature-flags, PUT /admin/feature-flags/{key}
// Topics are created and retired, never deleted.

export function fetchAllGoalContent() {
  return function (dispatch) {
    apiV2.get('/admin/goal-content')
      .then(response => {
        dispatch({
          type: FETCH_ALL_GOAL_CONTENT,
          payload: response.data.goal_contents,
        });
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
      });
  };
}

// Persist a new goal order (drag-to-reorder). `slugs` is the ordered list of
// goal_slugs; the server maps array position → display_order. The component
// updates its local order optimistically; on failure we re-fetch so the UI
// snaps back to the persisted order.
export function reorderGoalContent(slugs) {
  return function (dispatch) {
    return apiV2.put('/admin/goal-content/sort-order', { goals: slugs })
      .then(() => {
        // Subtle confirmation — fires on each drop without interrupting.
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: 'Order saved',
          timer: 1200,
          showConfirmButton: false,
        });
      })
      .catch(error => {
        dispatch(fetchAllGoalContent());
        errorHandler(dispatch, error.response, API_ERROR);
      });
  };
}

// Update one goal's content by slug.
export function saveGoalContent(slug, content, onSuccess) {
  return function (dispatch) {
    // Return the promise so the caller's spinner clears on actual completion.
    return apiV2.put(`/admin/goal-content/${slug}`, content)
      .then(() => {
        Swal.fire({
          icon: 'success',
          title: 'Saved!',
          timer: 1000,
        }).then(() => {
          onSuccess?.();
        });
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
      });
  };
}

// Create a topic. Resolves true on success, false on failure (the error is
// already surfaced), so the form can keep its data and re-enable Save.
export function createGoalContent(content, onSuccess) {
  return function (dispatch) {
    return apiV2.post('/admin/goal-content', content)
      .then(() => Swal.fire({ icon: 'success', title: 'Topic created!', timer: 1000 }))
      .then(() => {
        onSuccess?.();
        return true;
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
        return false;
      });
  };
}

export function fetchFeatureFlags() {
  return function (dispatch) {
    return apiV2.get('/admin/feature-flags')
      .then(response => {
        dispatch({ type: FETCH_FEATURE_FLAGS, payload: response.data.feature_flags });
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
      });
  };
}

// Resolves true when the server stored the change, false otherwise.
export function setFeatureFlag(key, enabled) {
  return function (dispatch) {
    return apiV2.put(`/admin/feature-flags/${encodeURIComponent(key)}`, { enabled })
      .then(response => {
        dispatch({ type: UPDATE_FEATURE_FLAG, payload: response.data });
        return true;
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
        return false;
      });
  };
}
